import { useEffect } from "react";
import { LANG_CODES, DEFAULT_LANG, localizePath, splitLangPath } from "../i18n/core";

const SITE_URL = "https://livingfincacortesin.com";
const DEFAULT_OG_IMAGE =
  "https://images.pexels.com/photos/32843780/pexels-photo-32843780.png";

function upsertMeta(attrName, attrValue, content) {
  if (!content) return;
  const selector = `meta[${attrName}="${attrValue}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Per-page SEO head manager. Sets:
 *  - <title>
 *  - <meta name="description">
 *  - <link rel="canonical">   (strips query string + fragment)
 *  - og:title, og:description, og:url, og:image
 *  - twitter:title, twitter:description, twitter:image
 *
 *   useDocumentMeta({ title, description, image, canonical });
 */
export default function useDocumentMeta({
  title,
  description,
  image,
  canonical,
} = {}) {
  useEffect(() => {
    if (title) document.title = title;

    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);

    // Resolve image to absolute URL so social crawlers can fetch it
    let imgAbs = image || DEFAULT_OG_IMAGE;
    if (imgAbs && imgAbs.startsWith("/")) {
      imgAbs = `${SITE_URL}${imgAbs}`;
    }
    upsertMeta("property", "og:image", imgAbs);
    upsertMeta("name", "twitter:image", imgAbs);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);

    // Canonical URL — strip query string + fragment, map to prod domain
    const path = canonical || (window.location.pathname || "/");
    const cleanPath = path.split("?")[0].split("#")[0];
    const href = cleanPath.startsWith("http")
      ? cleanPath
      : `${SITE_URL}${cleanPath}`;

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", href);
    upsertMeta("property", "og:url", href);

    // hreflang alternates for every language version of this page
    const { lang, basePath } = splitLangPath(cleanPath.startsWith("http") ? new URL(cleanPath).pathname : cleanPath);
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    [...LANG_CODES, "x-default"].forEach((code) => {
      const alt = document.createElement("link");
      alt.setAttribute("rel", "alternate");
      alt.setAttribute("hreflang", code);
      alt.setAttribute("href", `${SITE_URL}${localizePath(basePath, code === "x-default" ? DEFAULT_LANG : code)}`);
      document.head.appendChild(alt);
    });
    upsertMeta("property", "og:locale", lang);
  }, [title, description, image, canonical]);
}
